import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { eur } from "@/lib/format";
import { OPERATION_STATUSES, ALL_OPERATION_STATUSES } from "@/lib/constants";
import { PageHeader, EmptyState, OperationStatusBadge, OperationProgress } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Eye, Workflow, AlertTriangle } from "lucide-react";

export default function OperationsInProgress() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    // Only ACTIVE operations: concluded ones ("Incasso completato") live in Operazioni Concluse.
    api.get("/operations", { params: { concluded: "false" } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setRows([]); setError(`Impossibile caricare le operazioni: ${apiErr(e.response?.data?.detail) || "riprova più tardi."}`); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const deduped = useMemo(() => {
    // One row per property: keep the single (most-advanced) active operation, never one per phase.
    const rank = (s) => { const i = ALL_OPERATION_STATUSES.indexOf(s); return i < 0 ? -1 : i; };
    const byProp = new Map();
    const noProp = [];
    for (const o of rows) {
      if (o.status === "Incasso completato") continue; // concluded ops belong to Operazioni Concluse
      if (!o.property_id) { noProp.push(o); continue; }
      const ex = byProp.get(o.property_id);
      if (!ex || rank(o.status) > rank(ex.status)) byProp.set(o.property_id, o);
    }
    return [...byProp.values(), ...noProp];
  }, [rows]);

  const filtered = useMemo(() => deduped.filter((o) => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (search && !`${o.property_label || ""} ${o.responsible_name || ""} ${o.next_action || ""} ${o.contact_name || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [deduped, search, statusFilter]);

  return (
    <div data-testid="operations-page">
      <PageHeader title="Operazioni" subtitle={`${filtered.length} operazioni`} />

      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca per immobile, agente, prossima azione..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="operations-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-64" data-testid="operation-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {ALL_OPERATION_STATUSES.filter((s) => s !== "Incasso completato").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {error && (
        <Card className="p-6 mb-4 border-rose-200 bg-rose-50" data-testid="operations-error">
          <div className="flex items-center gap-3 text-rose-700">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div className="flex-1 text-sm">{error}</div>
            <Button variant="outline" size="sm" onClick={load} data-testid="operations-retry">Riprova</Button>
          </div>
        </Card>
      )}

      {!error && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Immobile</TableHead><TableHead>Agente</TableHead>
                <TableHead className="min-w-[220px]">Avanzamento</TableHead>
                <TableHead>Prossima azione</TableHead>
                <TableHead className="text-right">Prezzo acquisto</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/operazioni/${o.id}`)} data-testid={`operation-row-${o.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-800">{o.property_label || "—"}</div>
                      {o.type && <div className="text-xs text-primary/80">{o.type}</div>}
                      {o.contact_name && <div className="text-xs text-muted-foreground">{o.contact_name}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{o.responsible_name || "—"}</TableCell>
                    <TableCell><OperationStatusBadge status={o.status} /><div className="mt-1.5"><OperationProgress status={o.status} type={o.type} showLabel={false} /></div></TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{o.next_action || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{eur(o.purchase_price)}</TableCell>
                    <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-8 w-8"><Eye className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {!loading && filtered.length === 0 && <EmptyState><div className="flex flex-col items-center gap-2"><Workflow className="h-8 w-8 text-muted-foreground" /><span>Nessuna operazione trovata per il filtro selezionato.</span></div></EmptyState>}
        </Card>
      )}
    </div>
  );
}
